import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Category,
  Constants as GridConstants,
  Icons as GridIcons,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { BsArrowRightCircle } from "react-icons/bs";
import { FiDownloadCloud, FiEdit } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../constants";
import { useAppState } from "../../context/StateContext";
import { Booking as IBooking } from "../../models/booking";
import { Company } from "../../models/company";
import { Customer } from "../../models/customer";
import { PurchaseOrder } from "../../models/purchase.order";
import { Filters, Searches } from "../../models/shipeezi.table";
import {
  capitalizeFirstLetter,
  capitalizeFirstLetterAllWorlds,
} from "../../util/capitalizeFirstLetter";
import { BookingModes, BookingStatus, ResourceName, UserTypes } from "../../util/enums";
import { Button } from "../Form";
import { Color } from "../Form/Button";
import BadgeBookingStatus from "../Generic/BadgeBookingStatus";
import renderSharedReference from "../Generic/SharedReference";
import { DefaultTooltip } from "../Generic/Tooltip";
import { MoreOptions } from "../MoreOptionsComponent/MoreOptions";
import TitlePage from "../TitlePage";
import useActionBooking from "./actions";
import CancelBookingModal, { CancelBookingConfirmationModalHandles } from "./CancelBookingModal";
import SendBookingFileModal, {
  SendBookingFileConfirmationModalHandles,
} from "./SendBookingFileModal";
import useStyles from "./styles";
import UnderReviewBookingModal, {
  ReviewBookingConfirmationModalHandles,
} from "./UnderReviewBookingModal";
import {
  isBookingUnavaliableToAction,
  isBookingUnavaliableToUnderReview,
  isBookingCancelled,
} from "./util";
import BookingSideView, { BookingViewHandles } from "./View";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum } from "Util/enums/accessRule";

const CORNOR_UP_RIGHT_ICON = require("../../assets/img/corner-up-right.svg");

interface BookingProps {}

const Booking: React.FC<BookingProps> = () => {
  const { t: translate } = useTranslation();
  const { currentUser, isFMVersion, isSupplierPortal, isSPVersion, userPermissions } =
    useAppState();
  const classes = useStyles();
  const history = useHistory();

  const {
    getAllBookings,
    useDownloadBookingFile,
    useDownloadBookingFiles,
    useCancelBooking,
    useSendBookingFilesMods,
    useReceiveAllBookings,
    useSubmitBookingUnderReview,
  } = useActionBooking();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [search, setSearch] = useState<Searches>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [searchFilters, setSearchFilters] = useState<Filters>();

  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [bookingPermission, setBookingPermission] = useState<{
    viewAll: boolean;
    viewOwn: boolean;
    edit: boolean;
    create: boolean;
    export: boolean;
  }>({
    viewAll: false,
    viewOwn: false,
    edit: false,
    create: false,
    export: false,
  });

  const {
    data,
    isLoading: loadingBookings,
    isRefetching: refetchingBookings,
    refetch: refetchBookings,
  } = getAllBookings(page, pageSize, search, searchFilters);

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setBookingPermission({
        viewAll: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_VIEW_ALL),
        viewOwn: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_VIEW_OWN),
        edit:
          checkResourceCan(userPermissions, ResourceName.BOOKING, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.BOOKING, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_ADD_NEW),
        export:
          checkResourceCan(userPermissions, ResourceName.BOOKING, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_BOOKING_EXPORT),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (data && data?.rows) {
      setBookings(data.rows);
      setTotalItems(data.count);
    }
  }, [data]);

  const selectedCancelledBookings = useMemo(
    () =>
      bookings.some(
        (booking) =>
          booking.statusShipment === BookingStatus.CANCELLED &&
          selectedItems.includes(booking.id ?? -1)
      ),
    [selectedItems]
  );

  const emptySelectedItems = useMemo(() => selectedItems?.length === 0, [selectedItems]);

  const cancelModalRef = useRef<CancelBookingConfirmationModalHandles>(null);
  const underReviewModalRef = useRef<ReviewBookingConfirmationModalHandles>(null);
  const sendBookingFileModalRef = useRef<SendBookingFileConfirmationModalHandles>(null);
  const sideViewRef = useRef<BookingViewHandles>(null);

  const {
    mutate: cancelBooking,
    isLoading: isCancelingBooking,
    variables: cancelBookingId,
  } = useCancelBooking();

  const {
    mutate: submitBookingForReview,
    isLoading: isUnderReviewBooking,
    variables: underReviewBooking,
  } = useSubmitBookingUnderReview();

  const {
    mutate: downloadBookingFile,
    isLoading: loadingDownloadBooking,
    variables: downloadingBookingFileId,
  } = useDownloadBookingFile();

  const {
    mutate: downloadBookingFiles,
    isLoading: loadingDownloadBookings,
    variables: downloadBookingFileIds,
  } = useDownloadBookingFiles();

  const {
    mutate: sendBookingFilesMods,
    isLoading: loadingBookingFilesSubmit,
    variables: loadingBookingFileIds,
  } = useSendBookingFilesMods();

  const { mutate: receiveAllBookings, isLoading: isReceivingBookings } = useReceiveAllBookings();

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setSearchFilters(value);
    setPage(0);
  };

  const handleSearch = (value: Searches) => {
    setSearch(value);
    setPage(0);
  };

  const renderShipmentMode = (shipmentType: string) => {
    return <span>{capitalizeFirstLetter(shipmentType)}</span>;
  };

  const renderOrigin = (booking: IBooking) => {
    if (!booking) return "";

    return booking.origin;
  };

  const renderDestination = (booking: IBooking) => {
    if (!booking) return "";

    return booking.destination;
  };

  const columns: Column[] = [
    {
      name: "containerId",
      title: `${translate("booking")} #`,
      searchIdentifier: "container_id",
      formatComponent: (reference: string, booking: IBooking) =>
        renderSharedReference(
          reference,
          currentUser?.companyId !== booking.companyId,
          translate("booking")
        ),
    },
    {
      name: "shipmentType",
      title: translate("mode"),
      formatComponent: (value: string) => value && renderShipmentMode(value),
      hideSearch: true,
    },
    ...(isSPVersion || isFMVersion
      ? [
          {
            name: "company",
            title: translate("supplier"),
            formatText: (company: Company) => company?.supplierAssociated?.name || "",
            searchIdentifier: "supplierAssociated.name",
          },
        ]
      : []),

    {
      name: "consignee",
      title: translate("consignee"),
      formatText: (consignee: Customer) => consignee?.fullName || "",
      hideSearch: true,
    },

    {
      name: "purchaseOrders",
      title: translate("orders"),
      formatComponent: (orders: PurchaseOrder[]) => {
        return (
          <MoreOptions
            options={orders.map((order) => order.reference)}
            title={translate("orders")}
            manyOptionsToShow={1}
          />
        );
      },
      searchIdentifier: "purchaseOrders.purchase_order_id",
    },
    {
      name: "totalQuantity",
      title: translate("ssccQty"),
      hideSearch: true,
    },

    {
      name: "origin",
      title: translate("origin"),
      formatComponent: (value, row) => renderOrigin(row),
    },
    {
      name: "destination",
      title: translate("destination"),
      formatComponent: (value, row) => renderDestination(row),
    },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string) =>
        status && <BadgeBookingStatus value={capitalizeFirstLetterAllWorlds(status)} />,
      hideSearch: true,
    },
  ];

  const filterCategories: Category[] = [
    {
      name: translate("mode"),
      column: "shipment_type",
      options: [
        { value: BookingModes.SEA, label: translate("sea") },
        { value: BookingModes.AIR, label: translate("air") },
        { value: BookingModes.ROAD, label: translate("road") },
      ],
    },
    {
      name: translate("status"),
      column: "status_shipment",
      options: [
        { value: BookingStatus.PENDING, label: translate("pending") },
        { value: BookingStatus.CONFIRMED, label: translate("confirmed") },
        { value: BookingStatus.CANCELLED, label: translate("cancelled") },
        { value: BookingStatus.UNDER_REVIEW, label: translate("underReview") },
        { value: BookingStatus.CFS_REVIEW, label: translate("cfsReview") },
        { value: BookingStatus.CFS_RECEIVED, label: translate("receivedByCFS") },
        { value: BookingStatus.RECEIVED, label: translate("received") },
      ],
    },
  ];

  const downloadBookingDataButton = useCallback(() => {
    if (!isFMVersion || currentUser?.typeUser === UserTypes.WAREHOUSE) return null;

    const isDisabled = selectedCancelledBookings || emptySelectedItems;

    return (
      <DefaultTooltip
        title={selectedCancelledBookings ? translate("cantDownloadCancelledBookings") : ""}
      >
        <div>
          <Button
            disabled={isDisabled}
            className={classes.downloadBookingButton}
            startIcon={<FiDownloadCloud color="#fff" />}
            customColor={Color.SECONDARY}
            loading={loadingDownloadBookings}
            onClick={() => downloadBookingFiles(selectedItems)}
          >
            {translate("downloadBookingData")}
          </Button>
        </div>
      </DefaultTooltip>
    );
  }, [selectedItems, loadingDownloadBookings, isFMVersion]);

  const markAsReceivedButton = useCallback(() => {
    if (!isFMVersion || currentUser?.typeUser === UserTypes.WAREHOUSE) return null;

    const hasSelectedInvalidStatuses = !!bookings.find(
      (booking) =>
        !(booking.statusShipment === BookingStatus.CFS_RECEIVED) &&
        selectedItems.includes(booking.id ?? -1)
    );

    const isDisabled = emptySelectedItems || hasSelectedInvalidStatuses || !bookings;

    return (
      <DefaultTooltip
        title={
          hasSelectedInvalidStatuses
            ? translate("onlyBookingsWithReceivedByCFSCanChangeToReceivedStatus")
            : ""
        }
      >
        <div>
          <Button
            disabled={isDisabled}
            className={classes.markAsReceivedButton}
            startIcon={<GridIcons.status color={isDisabled ? "#98A2B3" : "#3F6280"} />}
            customColor={Color.INFO}
            loading={isReceivingBookings}
            onClick={() =>
              receiveAllBookings(
                { bookingIds: selectedItems },
                {
                  onSuccess: () => {
                    refetchBookings();
                    setSelectedItems([]);
                  },
                }
              )
            }
          >
            {translate("changeStatusesTo")}: {translate(BookingStatus.RECEIVED)}
          </Button>
        </div>
      </DefaultTooltip>
    );
  }, [selectedItems, isReceivingBookings, isFMVersion]);

  const sendBookingFileButton = useCallback(() => {
    if (!isFMVersion || currentUser?.typeUser === UserTypes.WAREHOUSE) return null;

    const isDisabled = selectedCancelledBookings || emptySelectedItems;

    return (
      <DefaultTooltip
        title={selectedCancelledBookings ? translate("cantSubmitCancelledBookings") : ""}
      >
        <div>
          <Button
            disabled={isDisabled}
            className={classes.submitButton}
            startIcon={
              <img src={CORNOR_UP_RIGHT_ICON} width="16" height="16" alt="corner up right icon" />
            }
            customColor={Color.INFO}
            loading={loadingBookingFilesSubmit}
            onClick={() => sendBookingFileModalRef?.current?.open(selectedItems)}
          >
            {translate("resubmitToMODS")}
          </Button>
        </div>
      </DefaultTooltip>
    );
  }, [selectedItems, loadingBookingFilesSubmit, isFMVersion]);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: [
        "handleSendBookingFile",
        "handleMarkAsReceived",
        "handleBookingDownload",
        "handleNewRecord",
      ],
    },
    handleBookingDownload: downloadBookingDataButton(),
    handleMarkAsReceived: markAsReceivedButton(),
    handleSendBookingFile: sendBookingFileButton(),
    handleNewRecord:
      isShowAction(currentUser, bookingPermission.create) && !isSPVersion && !isFMVersion
        ? {
            text: translate("createBooking"),
            action: () =>
              history.push({
                pathname: `${PRIVATE_ROUTES.BOOKINGS_MNT}`,
                state: { viewOnly: false },
              }),
            tooltip: translate("createBooking"),
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
  };

  const editBooking = (id: number | string) => {
    history.push({
      pathname: `${PRIVATE_ROUTES.BOOKINGS_MNT}/${id}`,
      state: { viewOnly: false },
    });
  };

  const renderEditRowButton = (id: string | number, booking: IBooking) => {
    const { statusShipment } = booking;
    let isEditEnabled = false;

    if (isSupplierPortal) {
      switch (statusShipment) {
        case BookingStatus.PENDING:
        case BookingStatus.UNDER_REVIEW:
          isEditEnabled = true;
          break;
        default:
          break;
      }
    } else if (isFMVersion) {
      switch (statusShipment) {
        case BookingStatus.CONFIRMED:
        case BookingStatus.CFS_RECEIVED:
        case BookingStatus.CFS_REVIEW:
          isEditEnabled = true;
          break;
        default:
          break;
      }
    }

    if (!isBookingUnavaliableToAction(booking) && isEditEnabled) {
      return (
        <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
          <div>
            <FiEdit
              onClick={() => editBooking(id)}
              color="#475467"
              className={classes.button}
              size={18}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const viewBooking = (id: number | string) => {
    if (id && !Number.isNaN(Number(id))) sideViewRef.current?.open(Number(id));
  };

  const renderCancelBookingRowButton = useCallback(
    (id: number | string, row: IBooking) => {
      const bookingId = Number(id);
      const openCancelModal = () => cancelModalRef?.current?.open(bookingId, row.containerId);

      if (
        isBookingUnavaliableToAction(row) ||
        isSPVersion ||
        currentUser?.typeUser === UserTypes.WAREHOUSE
      )
        return null;
      if (isSupplierPortal) return null;

      if (isCancelingBooking && bookingId === cancelBookingId) {
        return <CircularProgress size={20} />;
      }

      return (
        <Tooltip title={translate("cancel")} placement="top">
          <div>
            <MdOutlineCancel
              size={20}
              className={classes.button}
              data-cy="canceledButton"
              color="#F97066"
              onClick={() => {
                openCancelModal();
              }}
            />
          </div>
        </Tooltip>
      );
    },
    [isSPVersion, isSupplierPortal, isCancelingBooking]
  );

  const cancelBookingOnSubmit = (id: number) => {
    const booking = bookings.find((booking) => Number(booking.id) === Number(id));

    if (!booking) return;
    cancelBooking(id, {
      onSuccess: () => {
        cancelModalRef?.current?.close();
        refetchBookings();
      },
    });
  };

  const underReviewBookingOnSubmit = (id: number) => {
    const booking = bookings.find((booking) => Number(booking.id) === Number(id));

    if (!booking) return;
    submitBookingForReview(
      {
        id,
        booking: {
          id,
          containerId: booking.containerId,
          companyId: booking.companyId,
          statusShipment: BookingStatus.UNDER_REVIEW,
        } as IBooking,
        overrideChangedOptions: { booking: true },
      },
      {
        onSuccess: () => {
          underReviewModalRef?.current?.close();
          refetchBookings();
        },
      }
    );
  };

  const sendBookingFileOnSubmit = (bookingIds: number[]) => {
    if (!bookingIds || !bookingIds?.length) return null;

    sendBookingFilesMods(bookingIds, {
      onSuccess: () => {
        sendBookingFileModalRef?.current?.close();
      },
    });
  };

  const renderDownloadBookingFileRowButton = useCallback(
    (id: number, row: IBooking) => {
      if (row?.statusShipment === BookingStatus.CANCELLED) return null;

      if (
        (loadingDownloadBooking && downloadingBookingFileId === id) ||
        (loadingDownloadBookings && downloadBookingFileIds?.includes(id))
      )
        return <CircularProgress size={20} />;

      const onClick = () => {
        downloadBookingFile(id);
      };

      return (
        <Tooltip title={translate("downloadBookingFile")}>
          <div onClick={onClick} className={classes.button}>
            <FiDownloadCloud
              size={GridConstants.defaultRowActionSize}
              color={GridConstants.defaultColorIcon}
            />
          </div>
        </Tooltip>
      );
    },
    [
      loadingDownloadBooking,
      loadingDownloadBookings,
      downloadingBookingFileId,
      downloadBookingFileIds,
    ]
  );

  const renderUnderReviewRowButton = useCallback(
    (id: number | string, row: IBooking) => {
      const bookingId = Number(id);

      if (
        !isFMVersion ||
        isBookingUnavaliableToUnderReview(row) ||
        currentUser?.typeUser === UserTypes.WAREHOUSE
      )
        return null;

      const onClick = () => {
        underReviewModalRef?.current?.open(bookingId);
      };

      if (isUnderReviewBooking && bookingId === underReviewBooking?.id) {
        return <CircularProgress size={20} />;
      }

      return (
        <Tooltip title={translate("underReview")}>
          <div onClick={onClick} className={classes.button}>
            <BsArrowRightCircle size={GridConstants.defaultRowActionSize} color={"#32D583"} />
          </div>
        </Tooltip>
      );
    },
    [isFMVersion, isUnderReviewBooking]
  );

  const renderSendBookingFileRowButton = useCallback(
    (id: number | string, row: IBooking) => {
      const bookingId = Number(id);

      if (!isFMVersion || isBookingCancelled(row) || currentUser?.typeUser === UserTypes.WAREHOUSE)
        return null;

      if (loadingBookingFilesSubmit && loadingBookingFileIds?.includes(bookingId)) {
        return <CircularProgress size={20} />;
      }

      return (
        <DefaultTooltip title={translate("resubmitToMODS")}>
          <div
            onClick={() => sendBookingFileModalRef?.current?.open([bookingId])}
            className={classes.button}
          >
            <img src={CORNOR_UP_RIGHT_ICON} width="16" height="16" alt="corner up right icon" />
          </div>
        </DefaultTooltip>
      );
    },
    [isFMVersion, loadingBookingFilesSubmit]
  );

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [
      {
        title: translate("cancel"),
        key: "cancel",
        buttonAndAction: renderCancelBookingRowButton,
      },
    ];

    if (isShowAction(currentUser, bookingPermission.edit)) {
      actions.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: renderEditRowButton,
      });
    }

    if (isShowAction(currentUser, bookingPermission.export)) {
      actions.push({
        title: translate("downloadBookingFile"),
        key: "downloadBookingFile",
        buttonAndAction: renderDownloadBookingFileRowButton,
      });
    }

    actions.push(
      {
        title: translate("underReview"),
        key: "underReview",
        buttonAndAction: renderUnderReviewRowButton,
      },
      {
        title: translate("resubmitToMODS"),
        key: "resubmitToMODS",
        buttonAndAction: renderSendBookingFileRowButton,
      }
    );

    return actions;
  };

  return (
    <>
      <div data-cy="shipments-grid">
        {/* <TitlePage title={translate("bookings")} /> */}
        <div className={classes.bookingGridWrapper}>
          <ShipeeziNewGrid
            data={bookings}
            totalItems={totalItems}
            loadingData={loadingBookings || refetchingBookings}
            namePage={translate("booking")}
            emptyData={
              loadingBookings ? translate("loadingBookings") : translate("noInformationToBeShown")
            }
            columns={columns}
            currentPage={page}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            onChangePageSize={handlePageSize}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
            generalActions={generalActions}
            rowActions={{
              orderBy: [
                "edit",
                "handleDetails",
                "downloadBookingFile",
                "resubmitToMODS",
                "underReview",
                "cancel",
              ],
              handleDetails: isShowAction(currentUser, bookingPermission.viewOwn)
                ? (rowId) => viewBooking(rowId)
                : undefined,
            }}
            rowActionsExtra={handleRowActionsExtraByPermission()}
            positionActionsColumn="right"
            selecteds={selectedItems}
            handleSelect={setSelectedItems}
            showSelection={totalItems !== 0}
            showFooterGrid={totalItems !== 0}
          />
        </div>
        <CancelBookingModal
          onSubmit={cancelBookingOnSubmit}
          loadingAction={isCancelingBooking}
          ref={cancelModalRef}
        />
        <UnderReviewBookingModal
          ref={underReviewModalRef}
          loadingAction={isUnderReviewBooking}
          onSubmit={underReviewBookingOnSubmit}
        />
        <SendBookingFileModal
          ref={sendBookingFileModalRef}
          loadingAction={loadingBookingFilesSubmit}
          onSubmit={sendBookingFileOnSubmit}
        />
        <BookingSideView ref={sideViewRef} />
      </div>
    </>
  );
};

export default Booking;
